/**
 * The browser half of multilingual values.
 *
 * Mirrors `App\Support\MultilingualValue`, `App\Support\Language` and
 * `App\Support\ValueLimits`. The server is the authority — it validates every
 * write and will reject anything this file waves through — but the editor needs
 * the same rules locally to count characters as they are typed and to say which
 * translations exist without a round trip.
 *
 * Keep the caps in step with `App\Support\ValueLimits`; `multilingual.test.js`
 * asserts the numbers so a change on one side fails loudly on the other.
 */

/** The 24 official languages of the European Union, in canonical order. */
export const LANGUAGES = [
    { code: 'bg', name: 'Bulgarian', native: 'Български' },
    { code: 'cs', name: 'Czech', native: 'Čeština' },
    { code: 'da', name: 'Danish', native: 'Dansk' },
    { code: 'de', name: 'German', native: 'Deutsch' },
    { code: 'el', name: 'Greek', native: 'Ελληνικά' },
    { code: 'en', name: 'English', native: 'English' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'et', name: 'Estonian', native: 'Eesti' },
    { code: 'fi', name: 'Finnish', native: 'Suomi' },
    { code: 'fr', name: 'French', native: 'Français' },
    { code: 'ga', name: 'Irish', native: 'Gaeilge' },
    { code: 'hr', name: 'Croatian', native: 'Hrvatski' },
    { code: 'hu', name: 'Hungarian', native: 'Magyar' },
    { code: 'it', name: 'Italian', native: 'Italiano' },
    { code: 'lt', name: 'Lithuanian', native: 'Lietuvių' },
    { code: 'lv', name: 'Latvian', native: 'Latviešu' },
    { code: 'mt', name: 'Maltese', native: 'Malti' },
    { code: 'nl', name: 'Dutch', native: 'Nederlands' },
    { code: 'pl', name: 'Polish', native: 'Polski' },
    { code: 'pt', name: 'Portuguese', native: 'Português' },
    { code: 'ro', name: 'Romanian', native: 'Română' },
    { code: 'sk', name: 'Slovak', native: 'Slovenčina' },
    { code: 'sl', name: 'Slovenian', native: 'Slovenščina' },
    { code: 'sv', name: 'Swedish', native: 'Svenska' },
];

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);

export const DEFAULT_LANGUAGE = 'en';

/** Types whose value is a map of translations. `image` is not. */
export const MULTILINGUAL_TYPES = ['short_text', 'long_text', 'tags', 'file'];

/** Characters per language. Mirrors ValueLimits::MAX_CHARS. */
export const MAX_CHARS = { short_text: 255, long_text: 7000 };

/**
 * Bytes per language — the second pass. Mirrors
 * ValueLimits::MAX_BYTES_PER_LANGUAGE.
 *
 * 7,000 x 2, so the character cap and this one meet exactly at Cyrillic: every
 * European script binds on characters, and bytes only take over for CJK
 * (~4,600 characters) and emoji (~3,500).
 */
export const MAX_BYTES_PER_LANGUAGE = 14000;

export const MAX_TAGS = 50;
export const MAX_TAG_CHARS = 64;

export const isMultilingualType = (type) => MULTILINGUAL_TYPES.includes(type);

export const charLimit = (type) => MAX_CHARS[type] ?? null;

export const languageName = (code) =>
    LANGUAGES.find((l) => l.code === code)?.name ?? code;

export const languageNative = (code) =>
    LANGUAGES.find((l) => l.code === code)?.native ?? code;

/** The base tag for a client's language string, or null if not one of the 24. */
export function normalizeLanguage(code) {
    if (typeof code !== 'string' || code === '') return null;
    const base = code.trim().split(/[-_]/)[0].toLowerCase();
    return LANGUAGE_CODES.includes(base) ? base : null;
}

/**
 * Is this stored value a map of translations, or a legacy value that merely
 * happens to be an object?
 *
 * Same two traps as the server: a legacy `tags` value is an ARRAY, which no
 * translation map is, and a legacy `file` value is an object of structural keys
 * ("snowflake", "name"). Every key must be a language code before the value is
 * read as translations — one that is not means the whole thing is a single
 * legacy value, which preserves it instead of shredding it into fake
 * translations.
 */
export function isLanguageMap(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
    const keys = Object.keys(value);
    if (keys.length === 0) return false;
    return keys.every((key) => LANGUAGE_CODES.includes(key));
}

/** Read any stored value as a translation map. */
export function toTranslations(value, defaultLanguage = DEFAULT_LANGUAGE) {
    const fallbackCode = normalizeLanguage(defaultLanguage) ?? DEFAULT_LANGUAGE;

    if (value === null || value === undefined || value === '') return {};
    if (Array.isArray(value) && value.length === 0) return {};

    // An EMPTY OBJECT is no translations, not a legacy value. PHP never has to
    // say this — `[]` is both an empty list and an empty map there — but in
    // JavaScript {} falls past isLanguageMap(), which cannot tell an empty map
    // from a non-language one. Left to the legacy branch it becomes the default
    // language's VALUE, and the editor renders "[object Object]" the moment an
    // author clears their last translation.
    if (typeof value === 'object' && Object.keys(value).length === 0) return {};

    if (isLanguageMap(value)) {
        return Object.fromEntries(
            Object.entries(value).filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
        );
    }

    // A value written before multilinguality belongs to the language the
    // ontology is authored in.
    return { [fallbackCode]: value };
}

/**
 * The value in one language, and where it actually came from.
 *
 * requested -> the ontology's default -> any translation present. `isFallback`
 * is what lets the UI say "shown in English — no Bulgarian translation" rather
 * than presenting one language as another.
 */
export function resolveValue(value, requested, defaultLanguage = DEFAULT_LANGUAGE) {
    const fallbackCode = normalizeLanguage(defaultLanguage) ?? DEFAULT_LANGUAGE;
    const translations = toTranslations(value, fallbackCode);
    const wanted = normalizeLanguage(requested) ?? fallbackCode;

    if (Object.prototype.hasOwnProperty.call(translations, wanted)) {
        return { value: translations[wanted], language: wanted, isFallback: false };
    }

    for (const candidate of [fallbackCode, ...LANGUAGE_CODES]) {
        if (Object.prototype.hasOwnProperty.call(translations, candidate)) {
            return { value: translations[candidate], language: candidate, isFallback: true };
        }
    }

    // Claiming a language when there is no translation would be a lie.
    return { value: null, language: null, isFallback: false };
}

/** Codes that carry a translation, in canonical order. */
export function translatedLanguages(value, defaultLanguage = DEFAULT_LANGUAGE) {
    const translations = toTranslations(value, defaultLanguage);
    return LANGUAGE_CODES.filter((code) => code in translations);
}

/**
 * Apply a patch: languages the patch names are replaced, null deletes one, and
 * languages it does not mention are left alone — the same merge the API does,
 * so the editor's optimistic state matches what the server will store.
 */
export function mergeTranslations(value, patch, defaultLanguage = DEFAULT_LANGUAGE) {
    const translations = { ...toTranslations(value, defaultLanguage) };

    for (const [language, next] of Object.entries(patch ?? {})) {
        const code = normalizeLanguage(language);
        if (code === null) continue;

        if (next === null || next === '' || (Array.isArray(next) && next.length === 0)) {
            delete translations[code];
            continue;
        }

        translations[code] = next;
    }

    return translations;
}

/**
 * Check one translation against its type's cap.
 *
 * Character counting uses the spread operator rather than `.length`, so an
 * emoji counts as one character here exactly as `mb_strlen` counts it as one
 * on the server. Using `.length` would count it as two and the editor would
 * disagree with the API about when a field is full.
 */
export function checkLength(type, value) {
    // First pass: characters, the limit an author can act on.
    const tooLong = type === 'tags' ? checkTags(value) : checkChars(type, value);
    if (tooLong !== null) return tooLong;

    // Second pass: bytes, what DynamoDB counts. Only reachable once characters
    // pass, so it fires exactly for the scripts where the two diverge.
    return checkBytes(type, value);
}

function checkChars(type, value) {
    const limit = charLimit(type);
    if (limit === null || value === null || value === undefined) return null;
    if (typeof value !== 'string' && typeof value !== 'number') return null;

    const length = countCharacters(String(value));
    if (length <= limit) return null;

    return `This text is ${length.toLocaleString()} characters; the limit is ${limit.toLocaleString()} per language.`;
}

/**
 * One translation's weight, and what that weight means.
 *
 * Turns the byte ceiling back into a number of characters in the script being
 * typed: "14,000 bytes" is not something anyone can write against, "about 3,500
 * emoji" is.
 */
function checkBytes(type, value) {
    if (!isMultilingualType(type)) return null;

    const bytes = countBytes(value);
    if (bytes <= MAX_BYTES_PER_LANGUAGE) return null;

    const subject = type === 'tags' ? 'These tags are' : 'This text is';
    const characters = type === 'tags'
        ? (Array.isArray(value) ? value.reduce((n, tag) => n + countCharacters(tag), 0) : 0)
        : countCharacters(value);

    let message = `${subject} ${bytes.toLocaleString()} bytes; the limit is `
        + `${MAX_BYTES_PER_LANGUAGE.toLocaleString()} bytes per language. `
        + 'Characters outside the Latin alphabet weigh more than one byte in UTF-8'
        + ' — Greek and Cyrillic 2, most CJK 3, emoji 4.';

    if (characters > 0) {
        const perCharacter = bytes / characters;
        const fits = Math.floor(MAX_BYTES_PER_LANGUAGE / perCharacter);
        message += ` At this text's ${Math.round(perCharacter * 10) / 10} bytes per character,`
            + ` about ${fits.toLocaleString()} characters fit`
            + ` (it is currently ${characters.toLocaleString()}).`;
    }

    return message;
}

/**
 * UTF-8 bytes, the way the server counts them.
 *
 * A plain string is measured as its own bytes; a list keeps its JSON, because
 * the brackets and commas really are part of what a tags value costs. Same
 * split as ValueLimits::byteWeight().
 */
export function countBytes(value) {
    if (value === null || value === undefined) return 0;

    const text = typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : JSON.stringify(value);

    return new TextEncoder().encode(text).length;
}

function checkTags(value) {
    if (!Array.isArray(value)) return null;

    if (value.length > MAX_TAGS) {
        return `This is ${value.length} tags; the limit is ${MAX_TAGS} per language.`;
    }

    for (const tag of value) {
        if (typeof tag !== 'string') continue;
        const length = countCharacters(tag);
        if (length > MAX_TAG_CHARS) {
            return `A tag is ${length} characters; the limit is ${MAX_TAG_CHARS} per tag.`;
        }
    }

    return null;
}

/** Characters as a person counts them, and as mb_strlen counts them. */
export function countCharacters(text) {
    return [...String(text ?? '')].length;
}

/**
 * How full one translation is, for the editor's counter.
 *
 * `state` drives the colour: "ok" until the field is nearly full, "near" in the
 * last tenth, "over" past the cap.
 */
export function lengthState(type, value) {
    const limit = charLimit(type);
    if (limit === null) return null;

    const characters = countCharacters(value ?? '');
    const bytes = countBytes(value ?? '');

    // WHICHEVER IS CLOSER TO ITS LIMIT is the one shown. A CJK author typing
    // against a character counter would watch it read 3,000 / 7,000 while the
    // field was in fact nearly full, and then be refused at save — the counter
    // has to track the constraint that will actually stop them.
    const byBytes = bytes / MAX_BYTES_PER_LANGUAGE > characters / limit;

    const used = byBytes ? bytes : characters;
    const cap = byBytes ? MAX_BYTES_PER_LANGUAGE : limit;
    const state = used > cap ? 'over' : used >= cap * 0.9 ? 'near' : 'ok';

    return {
        used,
        limit: cap,
        remaining: cap - used,
        state,
        // What the number means, so the label can say "bytes" when it is bytes.
        unit: byBytes ? 'bytes' : 'characters',
        characters,
        bytes,
    };
}
